// pages/facility/Facilities.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import CustomerLayout from '../CustomerLayout';
import { getFacilities } from '../../../../services/staffingService';
import NewFacilitySlideOver from './NewFacilitySlideOver';
import { Building2, MapPin, Briefcase, Search, Plus, Loader2, ChevronRight } from 'lucide-react';

// ── Facility card ─────────────────────────────────────────────────────────────
function FacilityCard({ facility, customerCompanyId }) {
  const history = useHistory();
  const { name, city, state, address, status, activeJobCount } = facility;
  const initial = (name || 'F').charAt(0).toUpperCase();

  return (
    <div
      onClick={() => history.push(`/customer-company/${customerCompanyId}/facilities/${facility._id}`)}
      className="group cursor-pointer rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition-all duration-200 hover:border-[#404880]/30 hover:shadow-md"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-[#404880]/10 text-[#404880] font-bold text-lg">
            {initial}
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 truncate group-hover:text-[#404880] transition-colors">
              {name || 'Unnamed Facility'}
            </h3>
            {(city || state) && (
              <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                <MapPin size={10} />
                {[city, state].filter(Boolean).join(', ')}
              </p>
            )}
          </div>
        </div>
        <ChevronRight size={16} className="text-gray-300 group-hover:text-[#404880] transition-colors flex-shrink-0 mt-0.5" />
      </div>

      {/* Address */}
      {address && (
        <p className="text-xs text-gray-400 mb-4 truncate">{address}</p>
      )}

      {/* Footer stats */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <Briefcase size={12} />
          <span>{activeJobCount ?? 0} active {activeJobCount === 1 ? 'job' : 'jobs'}</span>
        </div>
        {status && (
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
            status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
          }`}>
            {status}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Add Facility card ─────────────────────────────────────────────────────────
function AddFacilityCard({ onAdd }) {
  return (
    <div
      onClick={onAdd}
      className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-200 bg-white p-5 text-center transition-all duration-200 hover:border-[#CC457D]/50 hover:bg-[#CC457D]/5 min-h-[164px] group"
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 group-hover:bg-[#CC457D]/10 transition-colors mb-2">
        <Plus size={18} className="text-gray-400 group-hover:text-[#CC457D] transition-colors" />
      </div>
      <p className="text-sm font-medium text-gray-500 group-hover:text-[#CC457D] transition-colors">Add Facility</p>
      <p className="text-xs text-gray-400 mt-0.5">Register a new location</p>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const Facilities = () => {
  const { customerCompanyId } = useParams();
  const [facilities,    setFacilities]    = useState([]);
  const [search,        setSearch]        = useState('');
  const [loading,       setLoading]       = useState(true);
  const [slideOverOpen, setSlideOverOpen] = useState(false);

  useEffect(() => {
    if (customerCompanyId) fetchFacilities();
  }, [customerCompanyId]);

  const fetchFacilities = async () => {
    setLoading(true);
    const resp = await getFacilities(customerCompanyId);
    setFacilities(resp.data || []);
    setLoading(false);
  };

  const handleFacilityCreated = () => {
    setSlideOverOpen(false);
    fetchFacilities();
  };

  const filtered = facilities.filter(f => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      f.name?.toLowerCase().includes(q) ||
      f.city?.toLowerCase().includes(q) ||
      f.state?.toLowerCase().includes(q) ||
      f.address?.toLowerCase().includes(q)
    );
  });

  return (
    <CustomerLayout>
      <main className="flex-1 overflow-y-auto">
        <div className="p-6">
          {/* ── Page header */}
          <div className="mb-6 flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#404880]/10">
                <Building2 size={20} className="text-[#404880]" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Facilities</h1>
                <p className="text-sm text-gray-500">
                  {facilities.length > 0 ? `${facilities.length} location${facilities.length !== 1 ? 's' : ''}` : 'Manage your sites'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setSlideOverOpen(true)}
              className="flex items-center gap-1.5 h-9 px-4 bg-[#CC457D] text-white rounded-lg text-sm font-medium transition-opacity hover:opacity-90"
            >
              <Plus size={14} /> Add Facility
            </button>
          </div>

          {/* ── Search */}
          {facilities.length > 0 && (
            <div className="mb-5 relative max-w-sm">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search facilities…"
                className="w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-[#404880] focus:outline-none focus:ring-1 focus:ring-[#404880]"
              />
            </div>
          )}

          {/* ── Loading */}
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={28} className="animate-spin text-[#404880]" />
            </div>
          )}

          {/* ── Empty state */}
          {!loading && facilities.length === 0 && (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white py-20 text-center">
              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100">
                <Building2 size={24} className="text-gray-400" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900">No facilities yet</h3>
              <p className="mt-1 text-xs text-gray-500 max-w-xs">
                Add your first facility to start creating security work orders for specific locations.
              </p>
              <button
                onClick={() => setSlideOverOpen(true)}
                className="mt-5 rounded-lg bg-[#CC457D] px-5 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity"
              >
                Add your first facility
              </button>
            </div>
          )}

          {/* ── Card grid */}
          {!loading && facilities.length > 0 && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filtered.map(f => (
                <FacilityCard key={f._id} facility={f} customerCompanyId={customerCompanyId} />
              ))}
              <AddFacilityCard onAdd={() => setSlideOverOpen(true)} />
            </div>
          )}

          {!loading && facilities.length > 0 && filtered.length === 0 && (
            <p className="py-8 text-center text-sm text-gray-500">
              No facilities match &ldquo;{search}&rdquo;
            </p>
          )}
        </div>
      </main>

      <NewFacilitySlideOver
        open={slideOverOpen}
        onClose={() => setSlideOverOpen(false)}
        onCreated={handleFacilityCreated}
        customerCompanyId={customerCompanyId}
      />
    </CustomerLayout>
  );
};

export default Facilities;

